import type { ReactNode } from "react";
import { Link } from "wouter";

import { useMe } from "./me.js";

/**
 * The client-side gate on every admin screen.
 *
 * WHY THIS EXISTS AT ALL, when the comments it replaces were right that "hiding
 * is a courtesy and the server refuses regardless". They were right about most
 * admin endpoints and wrong about a whole class of them, and the difference is
 * not visible from the router.
 *
 * `/admin/cases` queried `GET /api/cases`, which is role-SCOPED rather than
 * admin-only. A client asking for it was not refused — they were correctly
 * served THEIR OWN cases, 200 — and the page then wrapped that answer in "Every
 * case on the platform" and "the admin scope on this list is unfiltered". The
 * disclosure was not of anyone else's data; it was a false statement about the
 * platform, assembled in the browser out of a correct response and copy written
 * for a different reader. No server check can catch that, because the server
 * answered the question it was asked.
 *
 * `/admin/roster` reads the same endpoint. It happens to be safe today only
 * because two SIBLING queries on the same screen are admin-only and 403 first,
 * so the page renders their error instead of the case data. That is protection
 * by accident, and it evaporates the moment someone reorders a loading guard.
 *
 * So the rule is now structural rather than per-screen: an admin route is gated
 * because it is an admin route, not because someone remembered. A new admin
 * screen added inside this wrapper inherits it; one added outside is visibly
 * outside, which is the point.
 *
 * THIS IS NOT THE SECURITY BOUNDARY and must never be mistaken for it. The
 * server's authorize() chokepoint is, and it is unchanged. This stops the
 * browser from composing a truthful response into an untruthful screen.
 */
export function AdminOnly({ children }: { children: ReactNode }) {
  const me = useMe();

  /**
   * While the role is unknown, render NOTHING rather than the children.
   *
   * Optimistically painting the admin screen and correcting it a moment later
   * would reproduce the defect in miniature — a non-admin would see the
   * platform-wide copy for exactly as long as the round trip takes, which is the
   * window that matters on a slow connection.
   */
  if (me.isPending) {
    return (
      <p className="muted" role="status">
        Checking your access…
      </p>
    );
  }

  /**
   * A failed `me` is NOT treated as admin. The query has `retry: false`, so this
   * covers a signed-in user with no account row (401) as well as an outage —
   * both of which must fail closed.
   */
  if (!me.data || me.data.role !== "admin") {
    return (
      <section>
        <p className="eyebrow">Internal operations</p>
        <h2>This screen is for platform operators</h2>
        <p role="alert">
          Your account does not run the platform, so there is nothing for you on
          this screen. Nothing is missing from your own — whatever you can see
          there is everything that concerns you.
        </p>
        <p>
          <Link href="/">Go back to your account</Link>
        </p>
      </section>
    );
  }

  return <>{children}</>;
}
